import { FC } from "react";
import styled from "@emotion/styled";
import { MdsIcon, MdsIconKind } from "@/design-system/components/icon";
import { NoteInfo } from "./session-artifact-note-renderer.utils";

interface Props {
  noteInfo: NoteInfo;
  onClick?: () => void;
}

export const MDSSessionArtifactNoteEmbed: FC<Props> = ({ noteInfo, onClick }) => {
  return (
    <StyledContainer onClick={onClick}>
      <StyledIconWrapper>
        <MdsIcon kind={MdsIconKind.Document} />
      </StyledIconWrapper>
      <StyledContentColumn>
        <StyledPrimaryLabel>{noteInfo.primaryLabel}</StyledPrimaryLabel>
        {noteInfo.secondaryLabel && (
          <StyledSecondaryLabel>{noteInfo.secondaryLabel}</StyledSecondaryLabel>
        )}
      </StyledContentColumn>
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.mediumLarge}px;
  border: 1px solid ${({ theme }) => theme.colors.grey.x50};
  background-color: ${({ theme }) => theme.colors.white};
  min-width: 300px;
  max-width: 300px;
  user-select: none;
  cursor: ${({ onClick }) => (onClick ? "pointer" : "default")};
  transition: 0.08s all;

  &:hover {
    border-color: ${({ theme, onClick }) =>
      onClick ? theme.colors.grey.x100 : theme.colors.grey.x50};
    box-shadow: ${({ onClick }) => (onClick ? "0px 1px 2px 0px rgba(0, 0, 0, 0.02)" : "none")};
  }

  &:active {
    border-color: ${({ theme, onClick }) =>
      onClick ? theme.colors.grey.x100 : theme.colors.grey.x50};
    box-shadow: ${({ onClick }) => (onClick ? "0px 1px 1px 0px rgba(0, 0, 0, 0.06)" : "none")};
  }
`;

const StyledIconWrapper = styled.div`
  width: 40px;
  height: 40px;
  background-color: ${({ theme }) => theme.colors.grey.x50};
  border-radius: ${({ theme }) => theme.borderRadius.mediumLarge}px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;
    color: ${({ theme }) => theme.colors.grey.x400};
  }
`;

const StyledContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs};
  flex: 1;
  min-width: 0;
`;

const StyledPrimaryLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.small};
  color: ${({ theme }) => theme.colors.grey.x600};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  line-height: ${({ theme }) => theme.lineHeights.small};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledSecondaryLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xsmall};
  color: ${({ theme }) => theme.colors.grey.x500};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  line-height: ${({ theme }) => theme.lineHeights.small};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
